import {
  Alert,
  Button,
  Checkbox,
  Checklist,
  Drawer,
  Field,
  FieldGroup,
  Input,
  Loader,
  Select
} from '@itds/elements/index.js';
import { RIDER_LABELS, STATES, TABLE_RATE_LETTERS } from '@itds/Constants';
import React, { useEffect, useState } from 'react';

import Api from '@carrier/utils/Api';
import PropTypes from 'prop-types';

const Edit = ({ rider, onClose, onSave, show }) => {
  const [data, setData] = useState({
    id: null,
    carrier_id: null,
    data_version_id: null,
    description: '',
    name: '',
    rider_type: 'adb',
    gender: 'Both',
    termination_terms: '',
    tobacco: 'Both',
    lowest_table_rate: '',
    highest_table_rate: '',
    max_flat_extra: '',
    face_amount_start: '',
    face_amount_end: '',
    benefit_coverage_calc_type: '',
    min_benefit_coverage: '',
    max_benefit_coverage: '',
    coverage_per_unit: '',
    min_units: '',
    max_units: '',
    use_quote_column_states: true,
    band_ids: [],
    product_ids: [],
    health_category_ids: [],
    modal_factor_id: '',
    age_start: '',
    age_end: ''
  });
  const [errors, setErrors] = useState([]);
  const [save_error, setSaveError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bands, setBands] = useState([]);
  const [products, setProducts] = useState([]);
  const [health_categories, setHealthCategories] = useState([]);
  const [modal_factors, setModalFactors] = useState([]);
  const [populate_data, setPopulateData] = useState({ ages: '', rates: '' });

  useEffect(() => {
    const new_rider = rider || {};
    const states = {};
    const age_rates = {};

    STATES.forEach(state => {
      const name = `state_${state.value}`;

      states[name] = new_rider[name];
    });

    [...Array(100)].forEach((_v, i) => {
      const name = `age_${i + 1}`;

      age_rates[name] = new_rider[name];
    });

    setData({
      id: new_rider.id,
      carrier_id: new_rider.carrier_id,
      data_version_id: new_rider.data_version_id,
      description: new_rider.description || '',
      name: new_rider.name || '',
      rider_type: new_rider.rider_type || 'adb',
      gender: new_rider.gender || 'Both',
      termination_terms: new_rider.termination_terms || '',
      tobacco: new_rider.tobacco || 'Both',
      lowest_table_rate: new_rider.lowest_table_rate || '',
      highest_table_rate: new_rider.highest_table_rate || '',
      max_flat_extra: new_rider.max_flat_extra || '',
      face_amount_start: new_rider.face_amount_start || '',
      face_amount_end: new_rider.face_amount_end || '',
      benefit_coverage_calc_type: new_rider.benefit_coverage_calc_type || '',
      min_benefit_coverage: new_rider.min_benefit_coverage || '',
      max_benefit_coverage: new_rider.max_benefit_coverage || '',
      coverage_per_unit: new_rider.coverage_per_unit || '',
      min_units: new_rider.min_units || '',
      max_units: new_rider.max_units || '',
      use_quote_column_states: new_rider.use_quote_column_states || true,
      band_ids: new_rider.band_ids || [],
      product_ids: new_rider.product_ids || [],
      health_category_ids: new_rider.health_category_ids || [],
      modal_factor_id: new_rider.modal_factor_id || '',
      age_start: new_rider.age_start || '',
      age_end: new_rider.age_end || '',
      ...states,
      ...age_rates
    });

    setErrors([]);

    if (new_rider.carrier_id && new_rider.data_version_id) {
      const params = {
        carrier_id: new_rider.carrier_id,
        data_version_id: new_rider.data_version_id
      };

      setLoading(true);

      Promise.all([
        Api.loadBands(params),
        Api.loadProducts(params),
        Api.loadHealthCategories(params),
        Api.loadModalFactors(params)
      ]).then(values => {
        setBands(values[0].data);
        setProducts(values[1].data);
        setHealthCategories(values[2].data);
        setModalFactors(values[3].data);
        setLoading(false);
      });
    }

    return () => {
      setData({});
    };
  }, [rider]);

  const handleInputChange = (name, e) => {
    setData({ ...data, [name]: e.target.value });
  };

  const handleCheckboxChange = (name, e) => {
    setData({ ...data, [name]: e.target.checked });
  };

  const handleChecklistChange = (name, selected) => {
    setData({ ...data, [name]: selected });
  };

  const handleStatesChange = selected_states => {
    const updated_states = {};

    STATES.forEach(state => {
      updated_states[`state_${state.value}`] = selected_states.includes(state.value);
    });

    setData({ ...data, ...updated_states });
  };

  const handlePopulateChange = (name, e) => {
    setPopulateData({ ...populate_data, [name]: e.target.value });
  };

  const handlePopulate = () => {
    const ages = populate_data.ages.split('\n');
    const rates = populate_data.rates.split('\n');
    const updated_rates = {};

    ages.forEach((a, i) => {
      const age = a.trim();
      const rate = rates[i];

      if (rate) {
        updated_rates[`age_${age}`] = rate.trim();
      }
    });

    setData({ ...data, ...updated_rates });
  };

  const handleSave = () => {
    const errors = [];

    if (!data.name) {
      errors.push('name');
    }

    setErrors(errors);

    if (!errors.length) {
      const { id, ...payload } = data;

      Api.saveRider(id, payload)
        .then(() => {
          onSave();
          setSaveError(false);
        })
        .catch(() => {
          setSaveError(true);
          setTimeout(() => {
            setSaveError(false);
          }, 2000);
        });
    }
  };

  return (
    <Drawer
      alignRight={true}
      footer={<Button onClick={handleSave}>{data.id ? 'Save Changes' : 'Create Rider'}</Button>}
      onClose={onClose}
      show={show}
      title={data.id ? 'Editing Rider' : 'New Rider'}
      wide={true}
    >
      {loading ? (
        <Loader />
      ) : (
        <>
          {save_error ? (
            <Alert type='error'>An error occurred while trying to save the rider.</Alert>
          ) : null}

          <Field label='Name'>
            <Input
              invalid={errors.includes('name')}
              onChange={handleInputChange.bind(null, 'name')}
              required={true}
              value={data.name || ''}
            />
          </Field>

          <Field label='Description' style={{ marginBottom: '15px' }}>
            <Input
              height={100}
              onChange={handleInputChange.bind(null, 'description')}
              placeholder='Enter rider description'
              type='textarea'
              value={data.description || ''}
            />
          </Field>

          <Field label='Termination Terms' style={{ marginBottom: '15px' }}>
            <Input
              height={100}
              onChange={handleInputChange.bind(null, 'termination_terms')}
              placeholder='Enter termination terms'
              type='textarea'
              value={data.termination_terms || ''}
            />
          </Field>

          <Field label='Type'>
            <Select
              onChange={handleInputChange.bind(null, 'rider_type')}
              options={Object.keys(RIDER_LABELS).map(key => ({
                value: key,
                label: RIDER_LABELS[key]
              }))}
              value={data.rider_type}
            />
          </Field>

          <Field label='Gender'>
            <Select
              onChange={handleInputChange.bind(null, 'gender')}
              options={[
                { value: 'Both', label: 'Any Gender' },
                { value: 'Male' },
                { value: 'Female' }
              ]}
              value={data.gender}
            />
          </Field>

          <Field label='Tobacco'>
            <Select
              onChange={handleInputChange.bind(null, 'tobacco')}
              options={[
                { value: 'Both', label: 'Any Tobacco' },
                { value: 'NonSmoker' },
                { value: 'Smoker' }
              ]}
              value={data.tobacco}
            />
          </Field>

          <Field label='Max Flat Extra'>
            <Input
              onChange={handleInputChange.bind(null, 'max_flat_extra')}
              type='number'
              value={data.max_flat_extra || ''}
            />
          </Field>

          <div style={{ display: 'flex' }}>
            <Field label='Lowest Table Rate' style={{ flex: 1, marginRight: 10 }}>
              <Select
                onChange={handleInputChange.bind(null, 'lowest_table_rate')}
                options={[{ value: '', label: 'None' }].concat(
                  TABLE_RATE_LETTERS.map(letter => ({ value: letter }))
                )}
                value={data.lowest_table_rate}
              />
            </Field>

            <Field label='Highest Table Rate' style={{ flex: 1 }}>
              <Select
                onChange={handleInputChange.bind(null, 'highest_table_rate')}
                options={[{ value: '', label: 'None' }].concat(
                  TABLE_RATE_LETTERS.map(letter => ({ value: letter }))
                )}
                value={data.highest_table_rate}
              />
            </Field>
          </div>

          <FieldGroup contained={true} label=''>
            {data.rider_type === 'adb' ? (
              <>
                <Field label='Calculation Type'>
                  <Select
                    onChange={handleInputChange.bind(null, 'benefit_coverage_calc_type')}
                    options={[
                      { value: '', label: 'Default' },
                      {
                        value: 'face_or_amount',
                        label: 'Face Amount (up to Max Benefit Coverage)'
                      },
                      {
                        value: 'face_3x_or_amount',
                        label: 'Face Amount x3 (up to Max Benefit Coverage'
                      }
                    ]}
                    value={data.benefit_coverage_calc_type}
                  />
                </Field>

                <div style={{ display: 'flex' }}>
                  <Field
                    description='The minimum amount of coverage required for the rider to be applied.'
                    label='Min Benefit Coverage'
                    style={{ flex: 1, marginRight: 10 }}
                  >
                    <Input
                      format='currency'
                      onChange={handleInputChange.bind(null, 'min_benefit_coverage')}
                      value={data.min_benefit_coverage || ''}
                    />
                  </Field>

                  <Field
                    description='The maximum amount of coverage that the rider will be applied to, regardless of the face amount.'
                    label='Max Benefit Coverage'
                    style={{ flex: 1 }}
                  >
                    <Input
                      format='currency'
                      onChange={handleInputChange.bind(null, 'max_benefit_coverage')}
                      value={data.max_benefit_coverage || ''}
                    />
                  </Field>
                </div>
              </>
            ) : null}

            <div style={{ display: 'flex ', marginBottom: -20 }}>
              <Field
                description='If the requested face amount is less than this value, then no quote will be returned.'
                label='Face Amount Start'
                style={{ flex: 1, marginRight: 10 }}
              >
                <Input
                  format='currency'
                  onChange={handleInputChange.bind(null, 'face_amount_start')}
                  value={data.face_amount_start || ''}
                />
              </Field>

              <Field
                description='If the requested face amount is greater than this value, then no quote will be returned.'
                label='Face Amount End'
                style={{ flex: 1 }}
              >
                <Input
                  format='currency'
                  onChange={handleInputChange.bind(null, 'face_amount_end')}
                  value={data.face_amount_end || ''}
                />
              </Field>
            </div>
          </FieldGroup>

          {['child', 'child_wop'].includes(data.rider_type) ? (
            <>
              <Field label='Coverage Per Unit'>
                <Input
                  format='currency'
                  onChange={handleInputChange.bind(null, 'coverage_per_unit')}
                  value={data.coverage_per_unit || ''}
                />
              </Field>

              <div style={{ display: 'flex' }}>
                <Field label='Min Units' style={{ flex: 1, marginRight: 10 }}>
                  <Input
                    onChange={handleInputChange.bind(null, 'min_units')}
                    type='number'
                    value={data.min_units || ''}
                  />
                </Field>

                <Field label='Max Units' style={{ flex: 1 }}>
                  <Input
                    onChange={handleInputChange.bind(null, 'max_units')}
                    type='number'
                    value={data.max_units || ''}
                  />
                </Field>
              </div>
            </>
          ) : null}

          <Field>
            <Checkbox
              checked={data.use_quote_column_states}
              label='Use Quote Rate States'
              onChange={handleCheckboxChange.bind(null, 'use_quote_column_states')}
            />
          </Field>

          <div style={{ display: 'flex' }}>
            <Field label='Age Start' style={{ flex: 1, marginRight: '10px' }}>
              <Input
                onChange={handleInputChange.bind(null, 'age_start')}
                type='number'
                value={data.age_start || ''}
              />
            </Field>

            <Field label='Age End' style={{ flex: 1 }}>
              <Input
                onChange={handleInputChange.bind(null, 'age_end')}
                type='number'
                value={data.age_end || ''}
              />
            </Field>
          </div>

          {data.use_quote_column_states ? null : (
            <>
              <Field label='States'>
                <Checklist
                  columns={3}
                  onChange={handleStatesChange}
                  options={STATES}
                  selected={STATES.filter(state => data[`state_${state.value}`]).map(s => s.value)}
                  showSelectAll={true}
                />
              </Field>
            </>
          )}

          <Field label='Modal Factor'>
            <Select
              onChange={handleInputChange.bind(null, 'modal_factor_id')}
              options={[{ value: '', label: 'Use Quote Rate Modal Factor' }].concat(
                modal_factors.map(mf => ({ value: mf.id, label: mf.name }))
              )}
              value={data.modal_factor_id}
            />
          </Field>

          <Field label='Bands'>
            <Checklist
              columns={2}
              onChange={handleChecklistChange.bind(null, 'band_ids')}
              options={bands.map(b => ({ value: b.id, label: b.band_name }))}
              selected={data.band_ids}
              showSelectAll={true}
            />
          </Field>

          <Field label='Products'>
            <Checklist
              columns={3}
              onChange={handleChecklistChange.bind(null, 'product_ids')}
              options={products.map(p => ({ value: p.id, label: p.product_name }))}
              selected={data.product_ids}
              showSelectAll={true}
            />
          </Field>

          <Field label='Health Categories'>
            <Checklist
              columns={3}
              onChange={handleChecklistChange.bind(null, 'health_category_ids')}
              options={health_categories.map(hc => ({
                value: hc.id,
                label: hc.carrier_health_category
              }))}
              selected={data.health_category_ids}
              showSelectAll={true}
            />
          </Field>

          <FieldGroup contained={true} label='Age Rates'>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Field label='New Line Deliminated Ages' style={{ width: '49%' }}>
                <Input
                  height={200}
                  onChange={handlePopulateChange.bind(null, 'ages')}
                  type='textarea'
                  value={populate_data.ages || ''}
                />
              </Field>

              <Field label='New Line Deliminated Rates' style={{ width: '49%' }}>
                <Input
                  height={200}
                  onChange={handlePopulateChange.bind(null, 'rates')}
                  type='textarea'
                  value={populate_data.rates || ''}
                />
              </Field>
            </div>

            <Button inline={true} onClick={handlePopulate} small={true}>
              Populate
            </Button>

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                marginTop: 30
              }}
            >
              {[...Array(100)].map((_v, i) => {
                const num = i + 1;

                return (
                  <div
                    key={num}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '19%',
                      margin: '10px 0'
                    }}
                  >
                    <div style={{ width: 30, textAlign: 'right', marginRight: 10 }}>{num}</div>
                    <Input
                      onChange={handleInputChange.bind(null, `age_${num}`)}
                      value={data[`age_${num}`] || ''}
                    />
                  </div>
                );
              })}
            </div>
          </FieldGroup>
        </>
      )}
    </Drawer>
  );
};

Edit.propTypes = {
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  rider: PropTypes.object,
  show: PropTypes.bool
};

export default Edit;
